import React, { useState, useEffect, useRef } from 'react';
import { BreathingPattern } from '../types';
import { natureAmbience, NatureSoundType } from '../services/natureAmbienceService';

export const DEFAULT_BREATHING_PATTERNS: BreathingPattern[] = [
  {
    id: 'calm-4-7-8',
    name: '4-7-8 Relaxing Breath',
    badge: '⚡ Rapid Down-Regulation',
    reason: 'Elongated 8-second exhalation stimulates the vagus nerve and parasympathetic system to quickly ease heart rate and dissolve physical tension.',
    inhale: 4,
    holdAfterInhale: 7,
    exhale: 8,
    holdAfterExhale: 0,
  },
  {
    id: 'box-4-4-4-4',
    name: 'Box Breathing (4-4-4-4)',
    badge: '🎯 Cadence & Equilibrium',
    reason: 'Equal 4-part pacing balances oxygen and carbon dioxide levels to restore mental clarity, center erratic timing, and steady focus.',
    inhale: 4,
    holdAfterInhale: 4,
    exhale: 4,
    holdAfterExhale: 4,
  },
  {
    id: 'coherent-5-5',
    name: '5-5 Resonant Coherence',
    badge: '🌊 Harmonic Coherence',
    reason: 'Symmetrical 5-second breath cycles optimize heart rate variability (HRV), align cardiopulmonary rhythms, and prolong calm flow state.',
    inhale: 5,
    holdAfterInhale: 0,
    exhale: 5,
    holdAfterExhale: 0,
  },
  {
    id: 'extended-4-6',
    name: '4-6 Gentle Calming',
    badge: '🍃 Gentle Rest',
    reason: 'Gentle 1:1.5 inhale-to-exhale pacing provides effortless, unhurried calming for daily stress and neuromuscular ease.',
    inhale: 4,
    holdAfterInhale: 0,
    exhale: 6,
    holdAfterExhale: 0,
  },
  {
    id: 'restorative-4-4-8',
    name: '4-4-8 Deep Restorative',
    badge: '🌌 Deep Restorative',
    reason: 'Deep 4-second hold followed by an 8-second slow release grounds body awareness and locks in deep alpha-wave relaxation.',
    inhale: 4,
    holdAfterInhale: 4,
    exhale: 8,
    holdAfterExhale: 0,
  },
];

type BreathingPhase = 'idle' | 'inhale' | 'hold-in' | 'exhale' | 'hold-out';

interface GuidedBreathingProps {
  recommendedPattern?: BreathingPattern;
}

export const GuidedBreathing: React.FC<GuidedBreathingProps> = ({ recommendedPattern }) => {
  // Use recommended pattern as initial default, or fallback to standard 4-6
  const initialPattern = recommendedPattern || DEFAULT_BREATHING_PATTERNS[3];
  const [activePattern, setActivePattern] = useState<BreathingPattern>(initialPattern);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [phase, setPhase] = useState<BreathingPhase>('idle');
  const [countdown, setCountdown] = useState<number>(initialPattern.inhale);

  // Sync when recommendedPattern updates from session data
  useEffect(() => {
    if (recommendedPattern) {
      setActivePattern(recommendedPattern);
      if (!isActive) {
        setCountdown(recommendedPattern.inhale);
      }
    }
  }, [recommendedPattern]);

  // Nature Ambience State
  const [ambienceEnabled, setAmbienceEnabled] = useState<boolean>(true);
  const [ambienceType, setAmbienceType] = useState<NatureSoundType>('rain');

  // Ref to track interval and prevent multiple concurrent timers
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const stopBreathing = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    natureAmbience.stop();
    setIsActive(false);
    setPhase('idle');
    setCountdown(activePattern.inhale);
  };

  const startBreathing = (patternToUse = activePattern) => {
    // Prevent duplicate timers
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    setIsActive(true);
    setPhase('inhale');
    setCountdown(patternToUse.inhale);

    // Play nature ambience if enabled
    if (ambienceEnabled) {
      natureAmbience.start(ambienceType);
    }

    let currentPhase: BreathingPhase = 'inhale';
    let currentCount = patternToUse.inhale;

    timerRef.current = setInterval(() => {
      currentCount -= 1;

      if (currentCount <= 0) {
        // Phase Progression Engine:
        // inhale -> (hold-in if > 0) -> exhale -> (hold-out if > 0) -> inhale
        if (currentPhase === 'inhale') {
          if (patternToUse.holdAfterInhale > 0) {
            currentPhase = 'hold-in';
            currentCount = patternToUse.holdAfterInhale;
          } else {
            currentPhase = 'exhale';
            currentCount = patternToUse.exhale;
          }
        } else if (currentPhase === 'hold-in') {
          currentPhase = 'exhale';
          currentCount = patternToUse.exhale;
        } else if (currentPhase === 'exhale') {
          if (patternToUse.holdAfterExhale > 0) {
            currentPhase = 'hold-out';
            currentCount = patternToUse.holdAfterExhale;
          } else {
            currentPhase = 'inhale';
            currentCount = patternToUse.inhale;
          }
        } else if (currentPhase === 'hold-out') {
          currentPhase = 'inhale';
          currentCount = patternToUse.inhale;
        }

        setPhase(currentPhase);
      }

      setCountdown(currentCount);
    }, 1000);
  };

  const handleSelectPattern = (pat: BreathingPattern) => {
    const wasRunning = isActive;
    stopBreathing();
    setActivePattern(pat);
    setCountdown(pat.inhale);
    if (wasRunning) {
      setTimeout(() => {
        startBreathing(pat);
      }, 100);
    }
  };

  const handleToggleAmbience = (enabled: boolean) => {
    setAmbienceEnabled(enabled);
    if (isActive) {
      if (enabled) {
        natureAmbience.start(ambienceType);
      } else {
        natureAmbience.stop();
      }
    }
  };

  const handleSelectAmbienceType = (type: NatureSoundType) => {
    setAmbienceType(type);
    if (isActive && ambienceEnabled) {
      natureAmbience.setType(type);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      natureAmbience.stop();
    };
  }, []);

  // Determine circle style and animations based on current phase and duration
  const getCircleStyle = (): React.CSSProperties => {
    if (phase === 'inhale') {
      return {
        transform: 'scale(1.3)',
        transition: `transform ${activePattern.inhale}s cubic-bezier(0.37, 0, 0.63, 1), background-color 1.5s ease, box-shadow ${activePattern.inhale}s ease`,
      };
    }
    if (phase === 'hold-in') {
      return {
        transform: 'scale(1.3)',
        transition: `transform 0.5s ease-out, background-color 0.5s ease`,
      };
    }
    if (phase === 'exhale') {
      return {
        transform: 'scale(0.82)',
        transition: `transform ${activePattern.exhale}s cubic-bezier(0.37, 0, 0.63, 1), background-color 1.5s ease, box-shadow ${activePattern.exhale}s ease`,
      };
    }
    if (phase === 'hold-out') {
      return {
        transform: 'scale(0.82)',
        transition: `transform 0.5s ease-out, background-color 0.5s ease`,
      };
    }
    return {
      transform: 'scale(1)',
      transition: 'transform 0.5s ease-out, background-color 0.5s ease',
    };
  };

  const getStatusLine = () => {
    if (phase === 'inhale') return 'Inhale slowly and deeply through your nose.';
    if (phase === 'hold-in') return 'Gently hold your breath with softness.';
    if (phase === 'exhale') return 'Exhale slowly and smoothly through mouth or nose.';
    if (phase === 'hold-out') return 'Rest peacefully before the next breath.';
    return 'Press Start Breathing to begin this personalized cadence.';
  };

  const isCurrentRecommended = recommendedPattern?.id === activePattern.id;

  // Format short pace sequence e.g. "4s In • 7s Hold • 8s Out"
  const getPaceSummaryBadge = (pat: BreathingPattern) => {
    const parts = [`${pat.inhale}s In`];
    if (pat.holdAfterInhale > 0) parts.push(`${pat.holdAfterInhale}s Hold`);
    parts.push(`${pat.exhale}s Out`);
    if (pat.holdAfterExhale > 0) parts.push(`${pat.holdAfterExhale}s Rest`);
    return parts.join(' • ');
  };

  return (
    <div
      id="guided-breathing-card"
      className="bg-gradient-to-b from-slate-950/85 to-slate-900/90 border border-teal-500/25 rounded-2xl p-4 sm:p-5 text-white relative overflow-hidden shadow-lg shadow-teal-950/20"
      aria-label="Guided Breathing Exercise"
    >
      {/* Subtle background glow */}
      <div
        className={`absolute -top-16 -right-16 w-52 h-52 rounded-full blur-3xl pointer-events-none transition-opacity duration-1000 ${
          phase === 'inhale'
            ? 'bg-teal-500/25 opacity-100'
            : phase === 'hold-in'
            ? 'bg-amber-500/25 opacity-100'
            : phase === 'exhale'
            ? 'bg-indigo-500/25 opacity-100'
            : phase === 'hold-out'
            ? 'bg-sky-500/25 opacity-100'
            : 'bg-teal-500/10 opacity-60'
        }`}
      />

      {/* Header with Pattern Badge */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-2.5 mb-3 sm:mb-3.5 relative z-10 min-w-0">
        <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-teal-500/20 border border-teal-500/30 flex items-center justify-center text-teal-300 shrink-0">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="w-3.5 h-3.5 sm:w-4 sm:h-4"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
              />
            </svg>
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
              <h4 className="text-xs sm:text-sm font-bold text-white tracking-tight truncate">Guided Breathing</h4>
              {isCurrentRecommended && (
                <span className="text-[9px] sm:text-[10px] font-bold px-1.5 sm:px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-300 border border-teal-500/30 flex items-center gap-1 shrink-0">
                  <span>✨</span> Session Match
                </span>
              )}
            </div>
            <span className="text-[10px] sm:text-[11px] text-teal-300/90 font-medium block truncate">
              {activePattern.name} • <span className="text-slate-300">{getPaceSummaryBadge(activePattern)}</span>
            </span>
          </div>
        </div>

        {/* Phase Pill */}
        <span
          className={`text-[9px] sm:text-[10px] font-bold uppercase tracking-wider px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full border transition-all self-start sm:self-center shrink-0 ${
            phase === 'inhale'
              ? 'bg-teal-500/20 text-teal-300 border-teal-500/40 shadow-sm shadow-teal-500/20'
              : phase === 'hold-in'
              ? 'bg-amber-500/20 text-amber-300 border-amber-500/40 shadow-sm shadow-amber-500/20'
              : phase === 'exhale'
              ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40 shadow-sm shadow-indigo-500/20'
              : phase === 'hold-out'
              ? 'bg-sky-500/20 text-sky-300 border-sky-500/40 shadow-sm shadow-sky-500/20'
              : 'bg-slate-800 text-slate-400 border-slate-700'
          }`}
        >
          {phase === 'inhale'
            ? 'Inhaling'
            : phase === 'hold-in'
            ? 'Holding'
            : phase === 'exhale'
            ? 'Exhaling'
            : phase === 'hold-out'
            ? 'Resting'
            : 'Ready'}
        </span>
      </div>

      {/* Personalized Recommendation Reason Banner */}
      <div className="bg-slate-950/70 border border-teal-500/20 rounded-xl p-2 sm:p-2.5 mb-2.5 sm:mb-3 text-xs text-slate-300 flex items-start gap-2 relative z-10 min-w-0">
        <span className="text-teal-400 font-bold shrink-0 mt-0.5 text-xs sm:text-sm">✦</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <span className="font-semibold text-teal-200 block text-[10px] sm:text-[11px] truncate">
              {activePattern.badge}
            </span>
            {!isCurrentRecommended && recommendedPattern && (
              <button
                type="button"
                onClick={() => handleSelectPattern(recommendedPattern)}
                className="text-[9px] sm:text-[10px] text-teal-300 hover:text-teal-100 bg-teal-950/80 hover:bg-teal-900/90 px-2 py-0.5 rounded-md border border-teal-500/40 transition cursor-pointer shrink-0 flex items-center gap-1"
                title="Switch back to the pattern dynamically recommended for this session"
              >
                <span>✨</span> Session Pick
              </button>
            )}
          </div>
          <p className="text-[10px] sm:text-[11px] text-slate-300 leading-snug">
            {activePattern.reason}
          </p>
        </div>
      </div>

      {/* Breathing Presets Selection Pills */}
      <div className="flex items-center gap-1.5 overflow-x-auto max-w-full pb-1.5 mb-2 no-scrollbar relative z-10 touch-pan-x">
        {DEFAULT_BREATHING_PATTERNS.map((defaultPat) => {
          // If this default pattern matches the recommendedPattern id, use the session-customized reason/badge
          const pat = (recommendedPattern && recommendedPattern.id === defaultPat.id) ? recommendedPattern : defaultPat;
          const isSelected = activePattern.id === pat.id;
          const isRec = recommendedPattern?.id === pat.id;

          const getShortLabel = (id: string, name: string) => {
            if (id === 'calm-4-7-8') return '4-7-8 Relax';
            if (id === 'box-4-4-4-4') return '4-4-4-4 Box';
            if (id === 'coherent-5-5') return '5-5 Coherent';
            if (id === 'extended-4-6') return '4-6 Gentle';
            if (id === 'restorative-4-4-8') return '4-4-8 Restorative';
            return name.split(' ')[0];
          };

          return (
            <button
              key={pat.id}
              type="button"
              onClick={() => handleSelectPattern(pat)}
              className={`px-2.5 py-1.5 rounded-lg text-[10px] sm:text-[11px] font-semibold transition-all whitespace-nowrap shrink-0 flex items-center gap-1.5 cursor-pointer ${
                isSelected && isRec
                  ? 'bg-teal-500/30 text-teal-100 border border-teal-400/80 shadow-sm shadow-teal-950/40 ring-1 ring-teal-400/50'
                  : isSelected && !isRec
                  ? 'bg-slate-800 text-slate-100 border border-slate-500/70 shadow-sm'
                  : !isSelected && isRec
                  ? 'bg-teal-950/40 text-teal-300 hover:text-teal-100 border border-teal-500/40 hover:border-teal-400/60'
                  : 'bg-slate-900/80 text-slate-400 hover:text-slate-200 border border-white/5 hover:border-white/15'
              }`}
            >
              {isRec && (
                <span className="text-[9px] sm:text-[10px] bg-teal-400/20 text-teal-200 px-1 py-0.2 rounded font-bold">
                  ✨ Rec
                </span>
              )}
              <span>{getShortLabel(pat.id, pat.name)}</span>
            </button>
          );
        })}
      </div>

      {/* Center Breathing Visualizer */}
      <div className="py-2.5 sm:py-4 flex flex-col items-center justify-center relative z-10 min-h-[160px] sm:min-h-[180px] overflow-hidden">
        {/* Animated breathing circle container */}
        <div className="relative flex items-center justify-center w-32 h-32 sm:w-40 sm:h-40">
          {/* Outer halo / ripple ring */}
          {isActive && (
            <div
              className={`absolute inset-0 rounded-full border transition-all pointer-events-none ${
                phase === 'inhale'
                  ? 'border-teal-400/30 scale-125 opacity-80'
                  : phase === 'hold-in'
                  ? 'border-amber-400/40 scale-130 opacity-90 animate-pulse'
                  : phase === 'exhale'
                  ? 'border-indigo-400/30 scale-95 opacity-40'
                  : 'border-sky-400/30 scale-90 opacity-40'
              }`}
            />
          )}

          {/* Main Breathing Circle */}
          <div
            id="breathing-circle"
            role="status"
            aria-live="polite"
            aria-atomic="true"
            style={getCircleStyle()}
            className={`w-28 h-28 sm:w-36 sm:h-36 rounded-full flex flex-col items-center justify-center text-center select-none shadow-xl transition-all ${
              phase === 'inhale'
                ? 'bg-gradient-to-tr from-teal-600/90 to-emerald-500/90 border-2 border-teal-200/60 text-white shadow-teal-500/30'
                : phase === 'hold-in'
                ? 'bg-gradient-to-tr from-amber-600/90 to-amber-500/90 border-2 border-amber-200/60 text-white shadow-amber-500/30'
                : phase === 'exhale'
                ? 'bg-gradient-to-tr from-indigo-700/85 to-purple-600/85 border-2 border-indigo-200/50 text-white shadow-indigo-500/30'
                : phase === 'hold-out'
                ? 'bg-gradient-to-tr from-sky-800/85 to-blue-700/85 border-2 border-sky-300/40 text-white shadow-sky-500/20'
                : 'bg-slate-800/90 border-2 border-slate-600/60 text-slate-300 shadow-black/40'
            }`}
          >
            {isActive ? (
              <>
                <span className="text-[11px] sm:text-sm font-bold tracking-wide uppercase text-white/95 drop-shadow-sm">
                  {phase === 'inhale'
                    ? 'Breathe In'
                    : phase === 'hold-in'
                    ? 'Hold'
                    : phase === 'exhale'
                    ? 'Breathe Out'
                    : 'Pause'}
                </span>
                <span className="text-2xl sm:text-4xl font-black text-white tracking-tight mt-0.5 drop-shadow-md">
                  {countdown}
                </span>
              </>
            ) : (
              <>
                <span className="text-xs font-semibold text-slate-300">Ready</span>
                <span className="text-[10px] text-slate-400 mt-0.5">Press Start</span>
              </>
            )}
          </div>
        </div>

        {/* Dynamic Status Line */}
        <p
          id="breathing-status-line"
          aria-live="polite"
          className="text-[11px] sm:text-xs text-slate-300 text-center mt-2.5 sm:mt-3.5 font-medium transition-opacity duration-300 min-h-[18px] max-w-sm px-2"
        >
          {getStatusLine()}
        </p>
      </div>

      {/* Action Controls */}
      <div className="pt-1 flex flex-col items-center gap-2.5 sm:gap-3 relative z-10">
        {!isActive ? (
          <button
            id="start-breathing-btn"
            type="button"
            onClick={() => startBreathing(activePattern)}
            className="w-full sm:w-auto px-5 sm:px-6 py-2 sm:py-2.5 bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 text-slate-950 font-bold text-xs sm:text-sm rounded-xl transition-all shadow-md shadow-teal-500/25 flex items-center justify-center gap-2 active:scale-95 cursor-pointer min-h-[38px]"
            aria-label="Start Guided Breathing Exercise"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-4 h-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M4.5 5.653c0-1.426 1.529-2.33 2.779-1.643l11.54 6.348c1.295.712 1.295 2.573 0 3.285L7.28 19.991c-1.25.687-2.779-.217-2.779-1.643V5.653z"
                clipRule="evenodd"
              />
            </svg>
            <span>Start {activePattern.name.split(' ')[0]} Breathing</span>
          </button>
        ) : (
          <button
            id="stop-breathing-btn"
            type="button"
            onClick={stopBreathing}
            className="w-full sm:w-auto px-5 sm:px-6 py-2 sm:py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white border border-slate-600 font-semibold text-xs sm:text-sm rounded-xl transition-all shadow-md flex items-center justify-center gap-2 active:scale-95 cursor-pointer min-h-[38px]"
            aria-label="Stop Guided Breathing Exercise"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-4 h-4 text-red-400"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M4.5 7.5a3 3 0 013-3h9a3 3 0 013 3v9a3 3 0 01-3 3h-9a3 3 0 01-3-3v-9z"
                clipRule="evenodd"
              />
            </svg>
            <span>Stop</span>
          </button>
        )}

        {/* Calm Nature Ambience Toggle & Selector */}
        <div
          id="nature-ambience-panel"
          className="w-full mt-1 pt-2 sm:pt-2.5 border-t border-slate-800/80 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-2.5 text-xs"
        >
          {/* Toggle Switch */}
          <div className="flex items-center justify-between w-full sm:w-auto gap-2 sm:gap-3">
            <div className="flex items-center gap-1.5 sm:gap-2">
              <span className="text-teal-400" aria-hidden="true">
                {ambienceType === 'rain' ? '🌧️' : ambienceType === 'forest' ? '🌲' : '🌊'}
              </span>
              <span className="text-slate-300 font-medium select-none text-[10px] sm:text-[11px]">
                Calm Nature Ambience
              </span>
            </div>

            <button
              id="toggle-nature-ambience-btn"
              type="button"
              role="switch"
              aria-checked={ambienceEnabled}
              aria-label="Toggle Calm Nature Ambience during breathing"
              onClick={() => handleToggleAmbience(!ambienceEnabled)}
              className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-400 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                ambienceEnabled ? 'bg-teal-500' : 'bg-slate-700'
              }`}
            >
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out ${
                  ambienceEnabled ? 'translate-x-4' : 'translate-x-0'
                }`}
              />
            </button>
          </div>

          {/* Sound selection pills */}
          {ambienceEnabled && (
            <div
              id="nature-sound-selector"
              className="flex items-center justify-center gap-1 bg-slate-900/90 border border-slate-800 p-1 rounded-xl w-full sm:w-auto"
              role="radiogroup"
              aria-label="Nature Ambience Sound Options"
            >
              <button
                type="button"
                role="radio"
                aria-checked={ambienceType === 'rain'}
                onClick={() => handleSelectAmbienceType('rain')}
                className={`flex-1 sm:flex-initial text-center px-2 sm:px-2.5 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-semibold transition-all cursor-pointer ${
                  ambienceType === 'rain'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 border border-transparent'
                }`}
              >
                Rain
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={ambienceType === 'forest'}
                onClick={() => handleSelectAmbienceType('forest')}
                className={`flex-1 sm:flex-initial text-center px-2 sm:px-2.5 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-semibold transition-all cursor-pointer ${
                  ambienceType === 'forest'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 border border-transparent'
                }`}
              >
                Forest
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={ambienceType === 'stream'}
                onClick={() => handleSelectAmbienceType('stream')}
                className={`flex-1 sm:flex-initial text-center px-2 sm:px-2.5 py-0.5 rounded-lg text-[10px] sm:text-[11px] font-semibold transition-all cursor-pointer ${
                  ambienceType === 'stream'
                    ? 'bg-teal-500/20 text-teal-300 border border-teal-500/40 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200 border border-transparent'
                }`}
              >
                Stream
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
